'use client';

import { useCallback, useMemo, useState, type RefObject, type TouchEventHandler } from 'react';

type UsePullToRefreshOptions = {
  onRefresh: () => Promise<void> | void;
  scrollRef: RefObject<HTMLElement | null>;
  threshold?: number;
  maxPullDistance?: number;
};

type PullToRefreshHandlers = {
  onTouchStart: TouchEventHandler<HTMLElement>;
  onTouchMove: TouchEventHandler<HTMLElement>;
  onTouchEnd: TouchEventHandler<HTMLElement>;
  onTouchCancel: TouchEventHandler<HTMLElement>;
};

type UsePullToRefreshResult = {
  pullProgress: number;
  pullDistance: number;
  isRefreshing: boolean;
  isPulling: boolean;
  isEnabled: boolean;
  handlers: PullToRefreshHandlers;
};

const isIosStandalonePwa = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const standaloneByMedia = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  const standaloneByNavigator = (navigator as Navigator & { standalone?: boolean }).standalone ?? false;
  const isStandalone = standaloneByMedia || standaloneByNavigator;
  const isIosDevice =
    /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  return isStandalone && isIosDevice;
};

export const usePullToRefresh = ({
  onRefresh,
  scrollRef,
  threshold = 80,
  maxPullDistance = 120,
}: UsePullToRefreshOptions): UsePullToRefreshResult => {
  const [startY, setStartY] = useState<number | null>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isEnabled = isIosStandalonePwa();

  const resetPullState = useCallback(() => {
    setStartY(null);
    setPullDistance(0);
    setIsPulling(false);
  }, []);

  const onTouchStart = useCallback<TouchEventHandler<HTMLElement>>(
    (event) => {
      if (!isEnabled || isRefreshing || event.touches.length !== 1) {
        return;
      }

      const scrollElement = scrollRef.current;
      if (!scrollElement || scrollElement.scrollTop > 0) {
        return;
      }

      setStartY(event.touches[0]?.clientY ?? null);
      setIsPulling(false);
      setPullDistance(0);
    },
    [isEnabled, isRefreshing, scrollRef],
  );

  const onTouchMove = useCallback<TouchEventHandler<HTMLElement>>(
    (event) => {
      if (!isEnabled || isRefreshing || startY === null || event.touches.length !== 1) {
        return;
      }

      const scrollElement = scrollRef.current;
      if (!scrollElement || scrollElement.scrollTop > 0) {
        resetPullState();
        return;
      }

      const currentY = event.touches[0]?.clientY ?? startY;
      const diff = currentY - startY;
      if (diff <= 0) {
        setIsPulling(false);
        setPullDistance(0);
        return;
      }

      const easedDistance = Math.min(maxPullDistance, diff * 0.5);
      setIsPulling(true);
      setPullDistance(easedDistance);
      event.preventDefault();
    },
    [isEnabled, isRefreshing, maxPullDistance, resetPullState, scrollRef, startY],
  );

  const onTouchEnd = useCallback<TouchEventHandler<HTMLElement>>(async () => {
    if (!isEnabled || isRefreshing || startY === null) {
      return;
    }

    const shouldRefresh = isPulling && pullDistance >= threshold;
    resetPullState();

    if (!shouldRefresh) {
      return;
    }

    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  }, [isEnabled, isPulling, isRefreshing, onRefresh, pullDistance, resetPullState, startY, threshold]);

  const onTouchCancel = useCallback<TouchEventHandler<HTMLElement>>(() => {
    resetPullState();
  }, [resetPullState]);

  const pullProgress = useMemo(() => {
    if (threshold <= 0) {
      return 0;
    }
    return Math.min(1, pullDistance / threshold);
  }, [pullDistance, threshold]);

  return {
    pullProgress,
    pullDistance,
    isRefreshing,
    isPulling,
    isEnabled,
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel,
    },
  };
};
